import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for infinite scroll using Intersection Observer API
 * @param {Function} onLoadMore - Callback function to load more items
 * @param {boolean} hasMore - Whether there are more items to load
 * @param {boolean} isLoading - Whether data is currently being loaded
 * @param {Object} options - Additional options for Intersection Observer
 * @returns {Object} - Observer ref to attach to sentinel element
 */
export const useInfiniteScroll = (onLoadMore, hasMore, isLoading, options = {}) => {
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const { threshold = 1.0, rootMargin = '0px' } = options;

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, isLoading]
  );

  useEffect(() => {
    const option = {
      threshold,
      rootMargin,
    };

    observerRef.current = new IntersectionObserver(handleObserver, option);
    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      observerRef.current.observe(currentSentinel);
    }

    return () => {
      if (observerRef.current && currentSentinel) {
        observerRef.current.unobserve(currentSentinel);
      }
    };
  }, [handleObserver, threshold, rootMargin]);

  return sentinelRef;
};
